import * as XLSX from "xlsx";

import type { Account, MoveLineInput } from "~/types/contabilidad";

export interface ParsedLine {
  rowNumber: number;
  accountCode: string;
  description: string;
  debit: number;
  credit: number;
  error?: string;
  accountId?: string;
}

export interface ParseResult {
  lines: ParsedLine[];
  errors: string[];
  totalDebit: number;
  totalCredit: number;
  isBalanced: boolean;
}

/**
 * Parse a number from various formats
 * Handles: empty, "-", comma as thousand separator, period as decimal
 */
function parseNumber(value: unknown): number {
  if (value === null || value === undefined || value === "" || value === "-") {
    return 0;
  }

  if (typeof value === "number") {
    return value;
  }

  const str = String(value).trim();
  if (str === "" || str === "-") {
    return 0;
  }

  // Remove thousand separators (dots in Chilean format) and convert comma to period
  // Chilean format: 1.000.000,00 -> 1000000.00
  // Also handle: 1,000,000.00 (US format)
  let normalized = str;

  // Count dots and commas to determine format
  const dots = (str.match(/\./g) || []).length;
  const commas = (str.match(/,/g) || []).length;

  if (
    dots > 1 ||
    (dots === 1 && commas === 1 && str.indexOf(".") < str.indexOf(","))
  ) {
    // Chilean format: dots as thousands, comma as decimal
    normalized = str.replace(/\./g, "").replace(",", ".");
  } else if (
    commas > 1 ||
    (commas === 1 && dots === 1 && str.indexOf(",") < str.indexOf("."))
  ) {
    // US format: commas as thousands, dot as decimal
    normalized = str.replace(/,/g, "");
  } else if (commas === 1 && dots === 0) {
    // Single comma - assume it's a decimal separator (European/Chilean)
    normalized = str.replace(",", ".");
  }

  const num = parseFloat(normalized);
  return isNaN(num) ? 0 : Math.abs(num);
}

/**
 * Parse xlsx or csv file and extract movement lines
 */
export async function parseMovimientoFile(
  file: File,
  accounts: Account[]
): Promise<ParseResult> {
  const errors: string[] = [];
  const lines: ParsedLine[] = [];

  // Create account code to ID map
  const accountMap = new Map<string, string>();
  accounts.forEach((acc) => {
    accountMap.set(acc.code, acc.id);
  });

  try {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });

    // Get first sheet
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
      errors.push("El archivo no contiene hojas de cálculo");
      return { lines, errors, totalDebit: 0, totalCredit: 0, isBalanced: true };
    }

    const sheet = workbook.Sheets[sheetName];
    if (!sheet) {
      errors.push("No se pudo leer la hoja de cálculo");
      return { lines, errors, totalDebit: 0, totalCredit: 0, isBalanced: true };
    }

    // Convert to JSON (array of arrays)
    const data = XLSX.utils.sheet_to_json<(string | number | null)[]>(sheet, {
      header: 1,
      defval: "",
    });

    if (data.length < 2) {
      errors.push(
        "El archivo debe tener al menos una fila de encabezado y una fila de datos"
      );
      return { lines, errors, totalDebit: 0, totalCredit: 0, isBalanced: true };
    }

    // Skip header row (row 0), process data rows
    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      if (!row || row.length === 0) continue;

      // Skip empty rows
      const accountCode = String(row[0] || "").trim();
      if (!accountCode) continue;

      const description = String(row[1] || "").trim();
      const debit = parseNumber(row[2]);
      const credit = parseNumber(row[3]);

      const parsedLine: ParsedLine = {
        rowNumber: i + 1, // 1-indexed for user display
        accountCode,
        description,
        debit,
        credit,
      };

      // Validate account exists
      const accountId = accountMap.get(accountCode);
      if (!accountId) {
        parsedLine.error = `Cuenta '${accountCode}' no existe en la empresa seleccionada`;
      } else {
        parsedLine.accountId = accountId;
      }

      // Validate that at least one of debit or credit has a value
      if (debit === 0 && credit === 0) {
        parsedLine.error = parsedLine.error
          ? `${parsedLine.error}. Debe o Haber debe tener un valor`
          : "Debe o Haber debe tener un valor";
      }

      lines.push(parsedLine);
    }

    if (lines.length === 0) {
      errors.push("No se encontraron líneas de datos válidas en el archivo");
    }
  } catch (error) {
    errors.push(
      `Error al leer el archivo: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }

  // Calculate totals
  const totalDebit = lines.reduce((sum, line) => sum + line.debit, 0);
  const totalCredit = lines.reduce((sum, line) => sum + line.credit, 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  return {
    lines,
    errors,
    totalDebit,
    totalCredit,
    isBalanced,
  };
}

/**
 * Convert parsed lines to MoveLineInput format for API submission
 */
export function toMoveLineInputs(lines: ParsedLine[]): MoveLineInput[] {
  return lines
    .filter((line) => line.accountId && !line.error)
    .map((line) => ({
      account_id: line.accountId!,
      description: line.description || null,
      debit: line.debit,
      credit: line.credit,
    }));
}

/**
 * Check if all lines are valid (no errors)
 */
export function hasValidationErrors(result: ParseResult): boolean {
  return result.errors.length > 0 || result.lines.some((line) => !!line.error);
}
